/**
 * Traducciones al español de los enums del backend. Fuente
 * única para mostrar estos valores en la UI. El gateway los devuelve como
 * `string`, así que se usan mapas `Record<string,string>` + `labelOf`, que
 * normaliza a mayúsculas y cae al valor crudo si no hay traducción.
 *
 * Las **variantes** de `Badge` (color) NO viven aquí: son un detalle de UI y se
 * resuelven en cada badge. Este módulo solo traduce.
 */

/** Devuelve la etiqueta en español de `value` según `map`, o el valor crudo. */
export const labelOf = (map: Record<string, string>, value?: string | null): string =>
  value ? (map[value.toUpperCase()] ?? value) : '—'

/** Estado del negocio (`Status`). Incluye alias legacy usados en la UI actual. */
export const BUSINESS_STATUS_LABELS: Record<string, string> = {
  POTENTIAL: 'Potencial',
  INITIAL_EVALUATION: 'Evaluación inicial',
  DEEP_EVALUATION: 'Evaluación profunda',
  PENDING_APPROVAL: 'Pendiente de aprobación',
  APPROVED: 'Aprobado',
  PLANNING: 'En planificación',
  INSTALLATION: 'En instalación',
  IN_DEPLOY: 'En despliegue',
  USSED: 'Operativo',
  DEVELOPMENT: 'En desarrollo',
  PAUSE: 'Pausado',
  SERVICE_CANCELLED: 'Servicio cancelado',
  NOT_INTERESTED: 'No interesado',
  // Alias legacy (no están en enums.md pero llegan en algunos datos).
  ACTIVE: 'Activo',
  INACTIVE: 'Inactivo',
  CANCELLED: 'Cancelado',
  CANCELED: 'Cancelado',
  PENDING: 'Pendiente',
}

/** Estado de pago del negocio (`PaymentStatus`). Incluye alias legacy. */
export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PENDING_PAYMENT: 'Pendiente de pago',
  PARTIALLY_PAID: 'Pago parcial',
  PAID: 'Pagado',
  CANCELLED: 'Cancelado',
  REFUNDED: 'Reembolsado',
  CLOSED: 'Cerrado',
  // Alias legacy.
  PENDING: 'Pendiente',
  OVERDUE: 'Vencido',
}

/** Tipo de licencia (`LicenseType`). */
export const LICENSE_TYPE_LABELS: Record<string, string> = {
  MONTHLY: 'Mensual',
  ANNUAL: 'Anual',
}

/** Estado de aprobación de la organización (`OrganizationApprovalStatus`). */
export const ORG_APPROVAL_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Activa',
  PARTIAL_ACTIVE: 'Parcialmente activa',
  NO_ACTIVE: 'Inactiva',
  // Alias legacy usados antes en OrganizationHeader.
  APPROVED: 'Aprobada',
  PENDING: 'Pendiente de aprobación',
  REJECTED: 'Rechazada',
}

/**
 * Trazas / registros de auditoría (`BusinessRecordType`). Espejo del enum de
 * Zeus (`controlserver/prisma/schema.prisma`). Mantener sincronizado: si Zeus
 * añade un tipo, añadir aquí su etiqueta o la UI mostrará el código crudo.
 */
export const BUSINESS_RECORD_TYPE_LABELS: Record<string, string> = {
  ORGANIZATION_CREATED: 'Organización creada',
  ORGANIZATION_EDITED: 'Organización editada',
  ORGANIZATION_DEACTIVATED: 'Organización desactivada',
  BUSINESS_CREATED: 'Negocio creado',
  BUSINESS_ACTIVATED: 'Negocio activado',
  BUSINESS_DEACTIVATED: 'Negocio desactivado',
  BUSINESS_CHANGE_STATUS: 'Cambio de estado del negocio',
  BUSINESS_CHANGE_PAYMENT_STATUS: 'Cambio de estado de pago',
  BUSINESS_MODULE_CHANGED: 'Módulos del negocio modificados',
  BUSINESS_PRICE_CHANGE: 'Cambio de precio de servicio',
  BUSINESS_CHANGE_LEVEL: 'Cambio de nivel de servicio',
  BUSINESS_CHANGE_PLANNEDAT: 'Cambio del día planificado de pago',
  BUSINESSFILE_CREATED: 'Archivo del negocio creado',
  BUSINESSFILE_DELETED: 'Archivo del negocio eliminado',
  DOCUMENT_TYPE_CREATED: 'Tipo de documento creado',
  DOCUMENT_TYPE_UPDATED: 'Tipo de documento actualizado',
  DOCUMENT_TYPE_DELETED: 'Tipo de documento eliminado',
  BUSINESS_CANCELLED_DELINQUENCY: 'Negocio cancelado por morosidad',
  BUSINESS_REACTIVATED_AFTER_PAYMENT: 'Negocio reactivado tras pago',
  BUSINESS_CANCELLED_DELINQUENCY_DECLINED_AUTORIZO: 'Cancelado por morosidad — autorizo denegado',
  BUSINESS_CANCELLED_DELINQUENCY_AUTORIZO_EXPIRED: 'Cancelado por morosidad — autorizo expirado',
  BILLING_PAYMENT_CANCELLED: 'Pago de factura cancelado',
  BILLING_PAYMENT_DELETED: 'Pago de factura eliminado',
  BUSINESS_DEACTIVATED_BY_PAYMENT_DELETION: 'Negocio desactivado por eliminación de pago',
  BUSINESS_MANUALLY_CANCELLED: 'Negocio cancelado manualmente',
  BUSINESS_MANUALLY_REACTIVATED: 'Negocio reactivado manualmente',
  ORGANIZATION_MANUALLY_CANCELLED: 'Organización cancelada manualmente',
  ORGANIZATION_MANUALLY_REACTIVATED: 'Organización reactivada manualmente',
  CONTACT_CREATED: 'Contacto creado',
  CONTACT_UPDATED: 'Contacto actualizado',
  CONTACT_DELETED: 'Contacto eliminado',
  BUSINESS_SET_AS_MAIN: 'Negocio establecido como principal',
  BUSINESS_MOVED_ORGANIZATION: 'Negocio movido de organización',
  USER_ZEUS_ROLES_UPDATED: 'Roles de usuario actualizados',
  USER_DEGRADED: 'Usuario degradado',
  BUSINESS_SERVICE_ADDED: 'Servicio agregado al negocio',
  BUSINESS_SERVICE_UPDATED: 'Servicio del negocio actualizado',
  BUSINESS_SERVICE_DELETED: 'Servicio del negocio eliminado',
  BILLING_OBSERVATIONS_UPDATED: 'Observaciones de facturación actualizadas',
  CASH_DISPATCH_REOPENED: 'Despacho de efectivo reabierto',
  TEMPORARY_BUSINESS_ACCESS_GRANTED: 'Acceso temporal concedido',
  TEMPORARY_BUSINESS_ACCESS_REVOKED: 'Acceso temporal revocado',
  TEMPORARY_BUSINESS_ACCESS_EXPIRED: 'Acceso temporal vencido',
}

/** Estado de factura (`StatusBilling`). Aún no se renderiza en la UI. */
export const BILLING_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendiente de pago',
  PENDING_PAYMENT: 'Pendiente de pago',
  PAID: 'Pagada',
  PARTIALLY_PAID: 'Pago parcial',
  REFUNDED: 'Reembolsada',
  CANCELLED: 'Cancelada',
  OVERDUE: 'Vencida',
}

/** Resolución de pago de factura (`BillingPaymentResolution`). */
export const BILLING_PAYMENT_RESOLUTION_LABELS: Record<string, string> = {
  NONE: 'Sin resolución',
  PENDING_DECISION: 'Esperando decisión',
  AUTORIZO_ACTIVE: 'Autorizo activo',
  DECLINED: 'Autorizo denegado',
}

/** Estado del autorizo (`BillingAutorizoLifecycleStatus`). */
export const BILLING_AUTORIZO_STATUS_LABELS: Record<string, string> = {
  REQUESTED: 'Solicitado',
  GRANTED: 'Concedido',
  DENIED: 'Denegado',
}

/** Origen del lead (`OriginLead`). Aún no se renderiza en la UI. */
export const ORIGIN_LEAD_LABELS: Record<string, string> = {
  ORGANIC_SEARCH: 'Búsqueda orgánica',
  ONLINE_ADVERTISING: 'Publicidad online',
  SOCIAL_MEDIA: 'Redes sociales',
  EMAIL_MARKETING: 'Email marketing',
  WEBSITE_CONTACT_FORM: 'Formulario de contacto web',
  VIRTUAL_EVENT_WEBINAR: 'Evento virtual / webinar',
  COLD_CALL: 'Llamada en frío',
  SALES_VISIT: 'Visita comercial',
  TRADE_SHOW_EVENT: 'Feria o evento',
  PRINT_ADVERTISING: 'Publicidad impresa',
  TELEMARKETING: 'Telemarketing',
  CUSTOMER_REFERRAL: 'Referido por cliente',
  EMPLOYEE_REFERRAL: 'Referido por empleado',
  PARTNER_REFERRAL: 'Referido por socio',
  CONSULTANT_REFERRAL: 'Referido por consultor',
  DIRECT_CONTACT: 'Contacto directo',
  RENEWAL_UPSELL: 'Renovación / upsell',
  COMPETITOR_CLIENT: 'Cliente de la competencia',
  OTHER: 'Otro',
}
